import { DragEndEvent } from "@dnd-kit/core";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { lazy, PropsWithChildren, ReactElement, Suspense } from "react";

import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { AI_ASSISTANT_WIDTH } from "../constants/layout.ts";
import { $isCopilotOpen } from "../stores/copilot.ts";
import { $isEntityCreationFormOpen } from "../stores/graph/entities.ts";
import { $graphDragEvent, $isPanelTransitioning } from "../stores/graph/ui.ts";
import { $userAppSettings } from "../stores/user-app-settings.ts";
import { ActionPayloads } from "./ActionPayloads.tsx";
import { Button } from "./catalyst/Button.tsx";
import { DndProvider } from "./DndProvider";
import { FloatingGraphTools } from "./FloatingGraphTools.tsx";
import { GlobalCopilot } from "./GlobalCopilot";
import { GraphLayoutsControls } from "./GraphLayoutsControls.tsx";
import { GraphMenuIcon } from "./GraphMenuIcon.tsx";
import { GraphSelectedElementDetails } from "./GraphSelectedElementDetails.tsx";
import { GraphToolbar } from "./GraphToolbar.tsx";

const EntityFormCompactPage = lazy(
  () => import("../pages/EntityFormCompactPage.tsx"),
);

export const GraphLayout = ({ children }: PropsWithChildren): ReactElement => {
  const isAIAssistantOpen = useStore($isCopilotOpen);

  const handleDragEnd = (event: DragEndEvent): void => {
    $graphDragEvent.set(event);
  };
  const userAppSettings = useStore($userAppSettings);
  const isEntityCreationFormOpen = useStore($isEntityCreationFormOpen);

  const handleRightPanelToggle = (): void => {
    // Set transition state to prevent zoom changes BEFORE any other changes
    $isPanelTransitioning.set(true);

    // Small delay to ensure transition state is set
    setTimeout(() => {
      $userAppSettings.setKey(
        "sidebarRightVisible",
        !userAppSettings.sidebarRightVisible,
      );

      // Clear transition state after a longer delay
      setTimeout(() => {
        $isPanelTransitioning.set(false);
      }, 500);
    }, 10);
  };

  // Calculate positioning to ensure equal spacing
  // Header height: h-14 (56px)
  // GraphToolbar top: top-20 (80px from top, so 24px gap from header)
  // Gap from header to panel: 24px (80px - 56px)
  // Available space for panel = 100vh - 56px (header) - 48px (gap) = 100vh - 104px
  const panelTopPosition = "top-4"; // Align with collapsed button
  const panelHeight = "h-[calc(100vh-104px)]";

  const rightPanelStyle = {
    right: isAIAssistantOpen ? `${AI_ASSISTANT_WIDTH + 32}px` : "1rem", // 1rem ≈ Tailwind's right-4
    transition: "all 300ms ease-in-out",
  };

  return (
    <DndProvider onDragEnd={handleDragEnd}>
      <div
        className="relative h-screen w-full overflow-hidden bg-black transition-all duration-300 ease-in-out"
        style={{
          width: `calc(100% - ${isAIAssistantOpen ? AI_ASSISTANT_WIDTH : 0}px)`,
        }}
      >
        {children}
      </div>

      {/* Top Left Controls - Menu Icon and Graph Toolbar */}
      <div className="fixed left-4 top-4 z-50 flex items-center gap-2">
        <GraphMenuIcon />
        <GraphToolbar />
      </div>

      {/* Fixed Right Side Panel Expand Button - always visible when panel is closed */}
      {!userAppSettings.sidebarRightVisible && (
        <div
          className={clsx(
            "fixed z-50 transition-all duration-300 ease-in-out",
            // When Copilot is closed, its button sits at right-4/top-4.
            // Stack this button just below so both are usable.
            isAIAssistantOpen ? "right-[400px] top-4" : "right-4 top-14",
          )}
        >
          <Button
            className="ring-.75 rounded-lg bg-zinc-900/90 p-2 shadow-lg ring-zinc-600 transition-colors duration-200 hover:bg-zinc-800/90"
            onClick={handleRightPanelToggle}
            plain
            size="xs"
          >
            <ChevronLeftIcon className="text-zinc-500" />
          </Button>
        </div>
      )}

      {/* Floating Graph Tools */}
      <FloatingGraphTools />

      {/* Bottom Left Layout Controls - aligned with notes container */}
      <div className="fixed bottom-4 left-4 z-50">
        <div className="ring-.75 rounded-lg bg-zinc-900/90 p-2 shadow-lg ring-zinc-600">
          <GraphLayoutsControls isDisabled={false} />
        </div>
      </div>

      {/* Floating Right Sidebar */}
      {!isEntityCreationFormOpen && (
        <>
          {/* Always render the panel container, but control visibility with height and opacity */}
          <div
            className={clsx(
              "fixed",
              panelTopPosition,
              "z-40 transition-all duration-300 ease-in-out",
              userAppSettings.sidebarRightVisible
                ? "w-80 opacity-100"
                : "w-0 overflow-hidden opacity-0",
            )}
            style={rightPanelStyle}
          >
            <div
              className={`ring-.75 rounded-lg bg-zinc-900/90 p-3 shadow-lg ring-zinc-600 ${panelHeight} relative overflow-hidden`}
            >
              {/* Collapse button in top right of panel */}
              <Button
                className="absolute -right-1 -top-1 rounded bg-zinc-800/50 p-1 transition-colors duration-200 hover:bg-zinc-700/50"
                onClick={handleRightPanelToggle}
                plain
                size="xs"
              >
                <ChevronRightIcon className="h-4 w-4 text-zinc-500" />
              </Button>

              <div className="flex h-full flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <GraphSelectedElementDetails />
                </div>

                <div className="mt-3 flex-shrink-0">
                  <ActionPayloads />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Floating Entity Creation Form */}
      {isEntityCreationFormOpen && (
        <div
          className={clsx(
            "fixed",
            panelTopPosition,
            "z-40 transition-all duration-300 ease-in-out",
          )}
          style={rightPanelStyle}
        >
          <div
            className={`ring-.75 w-80 rounded-lg bg-zinc-900/90 p-3 shadow-lg ring-zinc-600 ${panelHeight} overflow-y-auto`}
          >
            <Suspense fallback={<LoadingIndicator />}>
              <EntityFormCompactPage />
            </Suspense>
          </div>
        </div>
      )}
      <GlobalCopilot />
    </DndProvider>
  );
};
